<script setup lang="ts">
import { useWithAuthApi } from '@/api/dupAuth/withAuth/withAuth'
import CommonDialog from '@/components/common/dialog/CommonDialog.vue'
import { useUserDeleteConfirmDialog } from '@/composables/userManagement/useUserDeleteConfirmDialog'
import { useUserManagement } from '@/composables/userManagement/useUserManagement'
import MiiaFwConfig from '@/configs/config'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { setLoadingView } from '@/features/miiaFw/utils/miiaFwUtils'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { getOrganizationCodeByUserId } from '@/utils/auth/user'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'

const { checkedItems } = defineProps<{ checkedItems: string[] }>()
const { openDialog, deleteUser } = useUserDeleteConfirmDialog()
const { itemsTyped } = useUserManagement()
const miiaFwStore = useMiiaFwStore()
const { govCode } = storeToRefs(miiaFwStore)
const { callGetOrganizationUsers } = useWithAuthApi()
defineExpose({
  openDialog
})
const commonDialog = ref<InstanceType<typeof CommonDialog> | null>(null)

/**
 * 現在のユーザーデータから団体ごとのユーザー総数を集計するcomputedプロパティ
 * Mapのキーが団体コード、値がその団体に所属するユーザー数となる
 */
const userCountPerOrganization = computed(() => {
  const counts = new Map<string, number>()
  itemsTyped.value.forEach(user => {
    if (user.organizationName) {
      const orgCode = getOrganizationCodeByUserId(user.userId)
      if (orgCode) {
        counts.set(orgCode, (counts.get(orgCode) ?? 0) + 1)
      }
    }
  })
  return counts
})

/**
 * 削除対象ユーザーから団体ごとのユーザー数を集計するcomputedプロパティ
 * Mapのキーが団体コード、値が削除対象ユーザー数となる
 */
const deletingCountPerOrganization = computed(() => {
  const counts = new Map<string, number>()
  checkedItems.forEach(userId => {
    const user = itemsTyped.value.find(u => u.userId === userId)
    if (user?.organizationName) {
      const orgCode = getOrganizationCodeByUserId(user.userId)
      if (orgCode) {
        counts.set(orgCode, (counts.get(orgCode) ?? 0) + 1)
      }
    }
  })
  return counts
})

/**
 * 削除によってユーザーがゼロになる団体のリストを計算するcomputedプロパティ
 * userCountPerOrganizationのユーザー総数とdeletingCountPerOrganizationの削除数が一致する団体コードを抽出する
 */
const organizationsBecomingEmpty = computed(() => {
  const result: string[] = []
  userCountPerOrganization.value.forEach((totalCount, orgCode) => {
    const deletingCount = deletingCountPerOrganization.value.get(orgCode) ?? 0
    if (totalCount > 0 && deletingCount === totalCount) {
      result.push(orgCode)
    }
  })
  return result
})

/**
 * 削除対象団体のメンバー取得を行う関数。
 * @param orgCode 団体コード
 * @returns 団体メンバーの配列
 */
async function getDeleteMember(orgCode: string) {
  const param = {
    system: MiiaFwConfig.config.sysId,
    govCode: govCode.value as string,
    organizationCode: orgCode,
    dupCategoryId: 'AeroPic'
  }
  return await callGetOrganizationUsers(param)
}

/**
 * ユーザー削除処理を実行する関数。
 * 親コンポーネントから渡された checkedItems を使用して削除を行う。
 */
async function onDelete() {
  try {
    setLoadingView(true)
    const deleteUsers: string[] = []
    for (const orgCode of organizationsBecomingEmpty.value) {
      const results = await getDeleteMember(orgCode)
      const members = results.map(result => result.userId)
      deleteUsers.push(...members)
    }
    deleteUsers.push(...checkedItems)
    // 同時処理数
    const concurrency = 3
    for (let i = 0; i < deleteUsers.length; i += concurrency) {
      // 3件ずつのスライスを作成
      const batch = deleteUsers.slice(i, i + concurrency)
      // batch内のdeleteUserをPromise.allで並列実行し完了まで待つ
      await Promise.all(batch.map(userId => deleteUser(userId)))
    }
    // 登録処理成功後に明示的にモーダルを閉じる処理
    commonDialog.value?.handleOkConfirmed()
  } catch (e) {
    throw GlobalError.chainError(e, ERROR_CODES._811204_DELETE_USER_ERROR)
  } finally {
    setLoadingView(false)
  }
}
</script>
<template>
  <CommonDialog
    size="md"
    ref="commonDialog"
    okTitle="削除"
    cancelTitle="閉じる"
    variant="danger"
    :isEmitOkEvent="true"
    @ok-event="onDelete"
  >
    <template #header> <h3 class="w-100 text-center">ユーザー削除確認</h3></template>
    <div class="container">
      <div>{{ `選択された${checkedItems.length}件のユーザーを削除します。` }}</div>
      <div>削除を実行すると元に戻すことはできませんがよろしいですか？</div>
      <!-- 削除処理で団体管理者が0になる場合 -->
      <div v-if="organizationsBecomingEmpty.length > 0" class="pt-3 fw-bold">
        警告：以下の団体は管理者以外のユーザーも削除されます
        <br />
        <ul>
          <li v-for="orgName in organizationsBecomingEmpty" :key="orgName">{{ orgName }}</li>
        </ul>
      </div>
    </div>
  </CommonDialog>
</template>

<style lang="scss" scoped></style>
